"use client";

import { useState, useEffect, useCallback } from "react";
import Link from "next/link";
import { usePostHog } from "posthog-js/react";

// All sample-engagement captures are fire-and-forget (never block the click /
// tab switch). `source` is fixed so these events sit alongside the
// rams_sample_viewed / cpp_sample_viewed CTAs that point visitors here.
const SAMPLES_SOURCE = "samples_page";

const documents = [
  {
    id: "site-induction",
    label: "Site Induction",
    description:
      "Covers site rules, emergency procedures, PPE requirements, and key contact information. Workers sign digitally before starting on site.",
    file: "/samples/sample-site-induction.pdf",
  },
  {
    id: "cpp",
    label: "Construction Phase Plan",
    description:
      "Site-specific CPP structured around CDM 2015 duties, covering site risks, welfare arrangements, emergency procedures, and management of the construction phase.",
    file: "/samples/sample-cpp.pdf",
  },
  {
    id: "rams",
    label: "RAMS",
    description:
      "Risk Assessment and Method Statement with site-specific hazards, control measures, COSHH section, and step-by-step safe systems of work.",
    file: "/samples/sample-rams.pdf",
  },
  {
    id: "emergency-plan",
    label: "Emergency Plan",
    description:
      "Emergency response plan covering fire strategy, assembly points, first aid arrangements, utility isolation points, nearest A&E, and RIDDOR reporting.",
    file: "/samples/sample-emergency-plan.pdf",
  },
  {
    id: "toolbox-talk",
    label: "Toolbox Talk",
    description:
      "Short, focused safety briefing on a single topic with key safety points, do's and don'ts, understanding checks, and an attendance record workers sign digitally.",
    file: "/samples/sample-toolbox-talk.pdf",
  },
] as const;

export function SamplesViewer() {
  const [selected, setSelected] = useState<(typeof documents)[number]>(
    documents[0],
  );
  const posthog = usePostHog();

  // Record the document the visitor lands on (the default tab), so the funnel
  // shows which sample first drew engagement, not only later tab switches.
  useEffect(() => {
    posthog?.capture?.("sample_document_viewed", {
      source: SAMPLES_SOURCE,
      document: documents[0].id,
      trigger: "initial",
    });
    // Fire once on mount for the default document.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posthog]);

  const handleSelect = useCallback(
    (doc: (typeof documents)[number]) => {
      setSelected(doc);
      posthog?.capture?.("sample_document_viewed", {
        source: SAMPLES_SOURCE,
        document: doc.id,
        trigger: "tab",
      });
    },
    [posthog],
  );

  return (
    <section className="px-4 pb-16 sm:px-6 sm:pb-20">
      <div className="mx-auto max-w-4xl">
        {/* Tab buttons */}
        <div className="flex gap-2 overflow-x-auto sm:gap-3">
          {documents.map((doc) => (
            <button
              key={doc.id}
              onClick={() => handleSelect(doc)}
              className={`shrink-0 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors ${
                selected.id === doc.id
                  ? "bg-accent text-white shadow-lg shadow-amber-600/25"
                  : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              {doc.label}
            </button>
          ))}
        </div>

        {/* Description */}
        <p className="mt-4 text-sm leading-relaxed text-gray-600">
          {selected.description}
        </p>

        {/* PDF viewer — embeds the same-origin sample PDF directly so the
            browser's built-in PDF viewer renders it inline. We previously used
            docs.google.com/gview, but Google stopped allowing that viewer in
            cross-origin iframes: it now answers with 204 + application/binary +
            X-Frame-Options: SAMEORIGIN, which browsers treat as a download on
            page load. Mobile browsers that can't embed a PDF fall back to the
            "Open the PDF" link below instead of forcing a download. */}
        <div className="mt-4 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg">
          <object
            key={selected.id}
            data={`${selected.file}#toolbar=1&navpanes=0&view=FitH`}
            type="application/pdf"
            aria-label={`Sample ${selected.label} document`}
            className="h-[600px] w-full sm:h-[700px]"
          >
            <div className="flex h-[600px] w-full flex-col items-center justify-center gap-3 p-8 text-center sm:h-[700px]">
              <p className="text-sm text-gray-600">
                Your browser can&apos;t preview this PDF inline.
              </p>
              <a
                href={selected.file}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  posthog?.capture?.("sample_downloaded", {
                    source: SAMPLES_SOURCE,
                    document: selected.id,
                  })
                }
                className="inline-flex items-center justify-center rounded-lg bg-accent px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-accent-dark"
              >
                Open the {selected.label} PDF
              </a>
            </div>
          </object>
        </div>

        {/* Actions */}
        <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
          <a
            href={selected.file}
            download
            onClick={() =>
              posthog?.capture?.("sample_downloaded", {
                source: SAMPLES_SOURCE,
                document: selected.id,
              })
            }
            className="inline-flex w-full items-center justify-center rounded-lg border border-gray-200 px-6 py-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 sm:w-auto"
          >
            Download this PDF
          </a>
          <Link
            href="/signup"
            onClick={() =>
              posthog?.capture?.("sample_create_clicked", {
                source: SAMPLES_SOURCE,
                document: selected.id,
              })
            }
            className="inline-flex w-full items-center justify-center rounded-lg bg-accent px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-amber-600/25 transition-colors hover:bg-accent-dark sm:w-auto"
          >
            Create yours &rarr;
          </Link>
        </div>
      </div>
    </section>
  );
}
